/**
 * Tenant Configuration Module
 *
 * Implements the Chameleon Architecture feature flag system.
 * Controls UI/API module availability based on tenant's digital maturity and subscription tier.
 */

// ============================================
// FEATURE FLAGS
// ============================================

/**
 * All available feature flags in the system.
 * Each flag controls visibility/availability of specific UI modules and API endpoints.
 */
export interface TenantFeatureFlags {
  // Core Features (always available)
  csv_onboarding: boolean;      // Universal Import Engine
  ai_analysis: boolean;         // OmniAsk AI assistance
  multi_store: boolean;         // Multi-store dashboard
  user_management: boolean;     // Basic user invitation

  // Integration Features (requires external systems)
  cctv_integration: boolean;    // VMS/Camera analytics
  erp_integration: boolean;     // ERP inventory sync
  pos_integration: boolean;     // POS transaction feed
  wms_integration: boolean;     // Warehouse management
  rfid_integration: boolean;    // RFID gate sensors

  // Advanced Features (premium tiers)
  custom_reports: boolean;      // Custom report builder
  realtime_alerts: boolean;     // Push notifications
  predictive_analytics: boolean; // ML-powered forecasting
  api_access: boolean;          // REST API for integrations
  white_label: boolean;         // Custom branding
  sso_integration: boolean;     // SAML/OIDC SSO

  // Control Tower Widgets
  live_heatmap: boolean;        // Real-time risk heatmap
  cashier_risk: boolean;        // Cashier anomaly detection
  phantom_inventory: boolean;   // Ghost inventory detection
  process_errors: boolean;      // Process error tracking

  // Chameleon Config Features (Phase 2.1)
  draggable_widgets: boolean;   // User-customizable widget layouts
  csv_snapshots: boolean;       // Point-in-time data versioning
  adaptive_dashboard: boolean;  // Maturity-aware widget swapping
}

/**
 * Default feature flags for new tenants (Starter tier)
 */
export const DEFAULT_FEATURE_FLAGS: TenantFeatureFlags = {
  // Core Features - enabled by default
  csv_onboarding: true,
  ai_analysis: true,
  multi_store: true,
  user_management: true,

  // Integration Features - disabled until connected
  cctv_integration: false,
  erp_integration: false,
  pos_integration: false,
  wms_integration: false,
  rfid_integration: false,

  // Advanced Features - premium only
  custom_reports: false,
  realtime_alerts: false,
  predictive_analytics: false,
  api_access: false,
  white_label: false,
  sso_integration: false,

  // Control Tower Widgets - tier dependent
  live_heatmap: false,
  cashier_risk: false,
  phantom_inventory: true,  // Available with CSV data
  process_errors: true,     // Available with CSV data

  // Chameleon Config Features - available from starter
  draggable_widgets: true,
  csv_snapshots: true,
  adaptive_dashboard: true,
};

// ============================================
// SUBSCRIPTION TIERS
// ============================================

export type SubscriptionTier = 'starter' | 'professional' | 'enterprise' | 'custom';

/**
 * Features enabled per subscription tier.
 * Used to auto-enable features when tenant upgrades.
 */
export const TIER_FEATURES: Record<SubscriptionTier, Partial<TenantFeatureFlags>> = {
  starter: {
    csv_onboarding: true,
    ai_analysis: true,
    multi_store: true,
    user_management: true,
    phantom_inventory: true,
    process_errors: true,
    draggable_widgets: true,
    csv_snapshots: true,
    adaptive_dashboard: true,
  },
  professional: {
    csv_onboarding: true,
    ai_analysis: true,
    multi_store: true,
    user_management: true,
    phantom_inventory: true,
    process_errors: true,
    custom_reports: true,
    realtime_alerts: true,
    cashier_risk: true,
    api_access: true,
    draggable_widgets: true,
    csv_snapshots: true,
    adaptive_dashboard: true,
  },
  enterprise: {
    csv_onboarding: true,
    ai_analysis: true,
    multi_store: true,
    user_management: true,
    phantom_inventory: true,
    process_errors: true,
    custom_reports: true,
    realtime_alerts: true,
    cashier_risk: true,
    api_access: true,
    predictive_analytics: true,
    live_heatmap: true,
    white_label: true,
    sso_integration: true,
    draggable_widgets: true,
    csv_snapshots: true,
    adaptive_dashboard: true,
  },
  custom: {
    // All features available, individually toggled
  },
};

/**
 * Maturity level requirements for integration features.
 * Features won't activate even if enabled unless maturity level is met.
 */
export const MATURITY_REQUIREMENTS: Partial<Record<keyof TenantFeatureFlags, number>> = {
  cctv_integration: 4,      // Requires VMS infrastructure
  erp_integration: 3,       // Requires ERP connection
  pos_integration: 3,       // Requires POS integration
  wms_integration: 4,       // Requires WMS system
  rfid_integration: 5,      // Requires RFID infrastructure
  live_heatmap: 4,          // Requires real-time data feeds
  predictive_analytics: 4,  // Requires historical data depth
};

// ============================================
// RBAC PERMISSIONS
// ============================================

/**
 * Resources that can be controlled via RBAC.
 */
export type RBACResource =
  | 'users'
  | 'stores'
  | 'alerts'
  | 'cases'
  | 'inventory'
  | 'products'
  | 'transactions'
  | 'reports'
  | 'settings'
  | 'imports'
  | 'audit_logs';

/**
 * Actions that can be performed on resources.
 */
export type RBACAction = 'create' | 'read' | 'update' | 'delete' | 'export' | 'import';

/**
 * Data scope for access control.
 */
export type DataScope = 'all' | 'assigned_stores' | 'own';

/**
 * Permission structure for a role.
 */
export type RolePermissions = {
  [resource in RBACResource]?: {
    [action in RBACAction]?: boolean;
  };
};

/**
 * System role definitions with default permissions.
 */
export const SYSTEM_ROLES: Record<string, { description: string; dataScope: DataScope; permissions: RolePermissions }> = {
  super_admin: {
    description: 'Full access to ALL tenants - platform administrator with cross-tenant visibility',
    dataScope: 'all',
    permissions: {
      users: { create: true, read: true, update: true, delete: true, export: true },
      stores: { create: true, read: true, update: true, delete: true, export: true },
      alerts: { create: true, read: true, update: true, delete: true, export: true },
      cases: { create: true, read: true, update: true, delete: true, export: true },
      inventory: { create: true, read: true, update: true, delete: true, export: true, import: true },
      products: { create: true, read: true, update: true, delete: true, export: true, import: true },
      transactions: { create: true, read: true, update: true, delete: true, export: true },
      reports: { create: true, read: true, update: true, delete: true, export: true },
      settings: { create: true, read: true, update: true, delete: true },
      imports: { create: true, read: true, update: true, delete: true },
      audit_logs: { read: true, export: true },
    },
  },
  tenant_admin: {
    description: 'Full administrative access within OWN tenant only - cannot view other tenants',
    dataScope: 'assigned_stores', // Key difference: tenant-scoped, not 'all'
    permissions: {
      users: { create: true, read: true, update: true, delete: true, export: true },
      stores: { create: true, read: true, update: true, delete: true, export: true },
      alerts: { create: true, read: true, update: true, delete: true, export: true },
      cases: { create: true, read: true, update: true, delete: true, export: true },
      inventory: { create: true, read: true, update: true, delete: true, export: true, import: true },
      products: { create: true, read: true, update: true, delete: true, export: true, import: true },
      transactions: { create: true, read: true, update: true, delete: true, export: true },
      reports: { create: true, read: true, update: true, delete: true, export: true },
      settings: { create: true, read: true, update: true, delete: true },
      imports: { create: true, read: true, update: true, delete: true },
      audit_logs: { read: true, export: true },
    },
  },
  regional_manager: {
    description: 'Manage multiple stores within assigned region',
    dataScope: 'assigned_stores',
    permissions: {
      users: { read: true },
      stores: { read: true, update: true },
      alerts: { create: true, read: true, update: true, export: true },
      cases: { create: true, read: true, update: true, export: true },
      inventory: { read: true, update: true, export: true, import: true },
      products: { read: true, update: true },
      transactions: { read: true, export: true },
      reports: { read: true, export: true },
      imports: { create: true, read: true },
      audit_logs: { read: true },
    },
  },
  store_manager: {
    description: 'Manage single store operations',
    dataScope: 'assigned_stores',
    permissions: {
      alerts: { create: true, read: true, update: true },
      cases: { create: true, read: true, update: true },
      inventory: { read: true, update: true, import: true },
      products: { read: true },
      transactions: { read: true },
      reports: { read: true },
      imports: { create: true, read: true },
    },
  },
  auditor: {
    description: 'Read-only access for compliance and auditing',
    dataScope: 'assigned_stores',
    permissions: {
      stores: { read: true },
      alerts: { read: true },
      cases: { read: true },
      inventory: { read: true },
      products: { read: true },
      transactions: { read: true },
      reports: { read: true },
      audit_logs: { read: true },
    },
  },
};

// ============================================
// TENANT CONFIGURATION TYPE
// ============================================

/**
 * Legacy data source configuration (for backward compatibility).
 */
export interface DataSourceConfig {
  hasERP: boolean;
  hasPOS: boolean;
  hasVMS: boolean;
  hasRFID?: boolean;
  hasAuditApp?: boolean;
}

/**
 * Theme configuration for white-labeling.
 */
export interface ThemeConfig {
  primaryColor?: string;
  accentColor?: string;
  logoUrl?: string;
  faviconUrl?: string;
  appName?: string;
}

/**
 * Billing configuration metadata.
 */
export interface BillingConfig {
  stripeCustomerId?: string;
  subscriptionId?: string;
  planId?: string;
  billingEmail?: string;
  trialEndsAt?: string;
  currentPeriodEnd?: string;
}

/**
 * Per-tenant business settings stored in tenant_settings JSONB column.
 */
export interface TenantSettings {
  thresholds?: {
    risk_high?: number;
    risk_medium?: number;
    shrinkage_max?: number;
    inventory_accuracy?: number;
    alert_aging?: number;
    alert_batch?: number;
  };
}

export const DEFAULT_TENANT_SETTINGS: TenantSettings = {
  thresholds: {
    risk_high: 80,
    risk_medium: 50,
    shrinkage_max: 1.5,
    inventory_accuracy: 98,
    alert_aging: 3,
    alert_batch: 5,
  },
};

/**
 * Complete tenant configuration.
 */
export interface TenantConfig {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  currencyCode: string;
  locale: string;

  // Digital maturity (1-5)
  maturityLevel: number;

  // Feature control
  featureFlags: TenantFeatureFlags;
  subscriptionTier: SubscriptionTier;

  // Legacy config
  config: DataSourceConfig;

  // Customization
  themeConfig: ThemeConfig;
  billingConfig: BillingConfig;

  // Per-tenant settings (thresholds, business rules)
  tenantSettings: TenantSettings;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if a feature is enabled for a tenant.
 * Considers both feature flags AND maturity level requirements.
 */
export function isFeatureEnabled(
  tenant: Pick<TenantConfig, 'featureFlags' | 'maturityLevel'>,
  feature: keyof TenantFeatureFlags
): boolean {
  // Check if feature flag is enabled
  if (!tenant.featureFlags[feature]) {
    return false;
  }

  // Check maturity level requirement
  const requiredMaturity = MATURITY_REQUIREMENTS[feature];
  if (requiredMaturity && tenant.maturityLevel < requiredMaturity) {
    return false;
  }

  return true;
}

/**
 * Get all enabled features for a tenant.
 */
export function getEnabledFeatures(
  tenant: Pick<TenantConfig, 'featureFlags' | 'maturityLevel'>
): (keyof TenantFeatureFlags)[] {
  return (Object.keys(tenant.featureFlags) as (keyof TenantFeatureFlags)[])
    .filter(feature => isFeatureEnabled(tenant, feature));
}

/**
 * Check if user has permission for a specific action on a resource.
 */
export function hasPermission(
  permissions: RolePermissions,
  resource: RBACResource,
  action: RBACAction
): boolean {
  return permissions[resource]?.[action] === true;
}

/**
 * Merge role permissions with user-specific overrides.
 */
export function mergePermissions(
  rolePermissions: RolePermissions,
  overrides: RolePermissions
): RolePermissions {
  const merged = { ...rolePermissions };

  for (const resource of Object.keys(overrides) as RBACResource[]) {
    merged[resource] = {
      ...merged[resource],
      ...overrides[resource],
    };
  }

  return merged;
}

/**
 * Get features to enable when upgrading to a tier.
 */
export function getFeaturesForTier(tier: SubscriptionTier): Partial<TenantFeatureFlags> {
  return TIER_FEATURES[tier] || {};
}

/**
 * Cascade feature flags based on maturity level.
 * Disables features that require a higher maturity level than provided.
 * This ensures consistency between maturity level and feature flags.
 */
export function cascadeFlagsForMaturity(
  currentFlags: TenantFeatureFlags,
  newMaturityLevel: number
): TenantFeatureFlags {
  const updatedFlags = { ...currentFlags };

  // Disable features that require higher maturity than the new level
  for (const [feature, requiredLevel] of Object.entries(MATURITY_REQUIREMENTS)) {
    if (requiredLevel !== undefined && newMaturityLevel < requiredLevel) {
      updatedFlags[feature as keyof TenantFeatureFlags] = false;
    }
  }

  return updatedFlags;
}

/**
 * Get list of features that will be disabled if maturity is lowered to the given level.
 * Useful for showing a confirmation dialog before changing maturity.
 */
export function getFeaturesAffectedByMaturityChange(
  currentFlags: TenantFeatureFlags,
  currentMaturity: number,
  newMaturity: number
): (keyof TenantFeatureFlags)[] {
  if (newMaturity >= currentMaturity) {
    return []; // Raising maturity doesn't disable features
  }

  const affected: (keyof TenantFeatureFlags)[] = [];

  for (const [feature, requiredLevel] of Object.entries(MATURITY_REQUIREMENTS)) {
    if (requiredLevel !== undefined &&
        currentFlags[feature as keyof TenantFeatureFlags] &&
        newMaturity < requiredLevel) {
      affected.push(feature as keyof TenantFeatureFlags);
    }
  }

  return affected;
}
